import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { X, Cpu, Network, Server, Play, Pause, Radio } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useWS } from '../contexts/WSContext'
import { FeedItem } from './LiveFeedView'
import { equipmentOf } from '../equipment'

// Linha rótulo/valor.
function Row({ k, v }) {
  if (!v) return null
  return (
    <div className="flex items-baseline justify-between gap-3 py-1 border-b border-slate-100 dark:border-slate-800 last:border-0">
      <span className="text-xs text-slate-500 dark:text-slate-400 shrink-0">{k}</span>
      <span className="text-xs font-medium text-slate-700 dark:text-slate-200 text-right break-all">{v}</span>
    </div>
  )
}

const IFACE_CLS = {
  up:   'bg-status-op/15 text-status-op',
  down: 'bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500',
}
const ifaceTxt = (t, state) =>
  state === 'up' ? t('ops:devices.iface_up')
  : state === 'down' ? t('ops:devices.iface_down')
  : state

// AdvancedDeviceModal: modal grande centralizado sobre a página com os detalhes
// do dispositivo (identificação de bootstrap + interfaces de rede) e uma caixa
// de eventos em tempo real que só transmite após apertar Play (nasce pausada,
// para economizar banda).
//
// Props: vmcName (id de bootstrap), displayName (nome no web), deviceInfo (se já
// disponível, ex.: do pending) OU fetchUrl (endpoint p/ buscar sob demanda, ex.:
// device adotado). onClose.
export default function AdvancedDeviceModal({ vmcName, displayName, deviceInfo, fetchUrl, onClose }) {
  const { t } = useTranslation()
  const { feed, devices, categories, mines, feedPaused, setFeedPaused } = useWS()

  const [info, setInfo] = useState(deviceInfo || null)
  useEffect(() => {
    if (deviceInfo || !fetchUrl) return
    fetch(fetchUrl).then(r => r.json()).then(setInfo).catch(() => setInfo({}))
  }, [fetchUrl, deviceInfo])

  const sys = info?.system || {}
  const net = info?.network || {}
  const ifaces = Array.isArray(net.interfaces) ? net.interfaces : []
  const os = [sys.os_id, sys.os_version].filter(Boolean).join(' ')

  const nameOf = (dn) => {
    const d = devices.find(x => x.name === dn)
    return (d && equipmentOf(d, categories, mines).name) || dn
  }

  // Nasce PAUSADO: os eventos só chegam após o Play. A pausa é do stream INTEIRO deste
  // navegador (o servidor para de mandar eventos ao cliente), então: o aviso fica na
  // caixa abaixo e no selo da topbar, e ao fechar restaura o estado anterior em vez de
  // forçar retomada (o botão de pausa do Eventos deixava de dizer a verdade).
  const [playing, setPlaying] = useState(false)
  const prevPaused = useRef(feedPaused)

  useEffect(() => {
    setFeedPaused(true)
    return () => setFeedPaused(prevPaused.current === true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const togglePlay = () => {
    setPlaying(p => {
      const next = !p
      setFeedPaused(!next)
      return next
    })
  }

  const events = useMemo(
    () => (playing ? feed.filter(m => m.deviceName === vmcName).slice(0, 60) : []),
    [playing, feed, vmcName]
  )

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.() }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev }
  }, [onClose])

  // Portal para o body: escapa de ancestrais com transform (ex.: o painel do
  // Drawer usa translate-x, que aprisionaria um fixed descendente).
  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/50" onClick={onClose} />
      <div className="relative w-full max-w-4xl h-[85vh] flex flex-col bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3 min-w-0">
            <Server size={18} className="text-brand shrink-0" />
            <div className="min-w-0">
              <h2 className="font-semibold text-slate-800 dark:text-slate-100 truncate">{t('ops:drawer.advanced_details')}</h2>
              <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{displayName && displayName !== vmcName ? `${displayName} · ` : ''}{vmcName}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 shrink-0"><X size={18} /></button>
        </div>

        <div className="flex-1 min-h-0 flex flex-col p-5 gap-4">
          <div className="grid gap-4 md:grid-cols-2 shrink-0">
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4">
              <p className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">
                <Cpu size={13} className="text-slate-400" /> {t('ops:devices.bootstrap_id')}
              </p>
              {displayName && displayName !== vmcName && <Row k={t('ops:drawer.web_name')} v={displayName} />}
              <Row k={t('ops:drawer.name_iot')} v={vmcName} />
              <Row k={t('ops:devices.fields.vendor')} v={sys.board_vendor} />
              <Row k={t('ops:devices.fields.board')} v={sys.board_name} />
              <Row k={t('ops:devices.fields.model')} v={sys.board_model} />
              <Row k={t('ops:devices.fields.platform')} v={sys.board_platform_version} />
              <Row k={t('ops:devices.fields.system')} v={os} />
              <Row k={t('ops:devices.fields.architecture')} v={sys.architecture} />
            </div>

            <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4">
              <p className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">
                <Network size={13} className="text-slate-400" /> {t('ops:devices.network_ifaces', { count: ifaces.length })}
              </p>
              {info === null ? (
                <p className="text-xs text-slate-400 dark:text-slate-500">{t('common:states.loading')}</p>
              ) : ifaces.length === 0 ? (
                <p className="text-xs text-slate-400 dark:text-slate-500">{t('ops:drawer.no_ifaces')}</p>
              ) : (
                <div className="space-y-1.5">
                  {ifaces.map((i, idx) => {
                    const cls = IFACE_CLS[i.state] || 'bg-slate-100 dark:bg-slate-800 text-slate-500'
                    return (
                      <div key={idx} className="flex items-center justify-between gap-2 text-xs">
                        <div className="min-w-0">
                          <span className="font-medium text-slate-700 dark:text-slate-200">{i.name}</span>
                          <span className="ml-2 font-mono text-slate-400 dark:text-slate-500">{i.mac}</span>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <span className="text-slate-400 dark:text-slate-500">{i.type}</span>
                          <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${cls}`}>{ifaceTxt(t, i.state)}</span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden flex-1 min-h-0 flex flex-col">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 shrink-0">
              <p className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 dark:text-slate-300">
                <Radio size={13} className="text-slate-400" /> {t('ops:drawer.live_events')}
                {playing && <span className="inline-flex items-center gap-1 text-green-700 dark:text-green-300"><span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" /> {t('ops:drawer.live')}</span>}
              </p>
              <button
                onClick={togglePlay}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-white dark:hover:bg-slate-800 transition-colors"
              >
                {playing ? <><Pause size={12} /> {t('common:actions.pause')}</> : <><Play size={12} /> {t('ops:drawer.play')}</>}
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin">
              {!playing ? (
                <div className="flex flex-col items-center justify-center h-full text-slate-400 dark:text-slate-500 gap-1 px-6 text-center">
                  <Play size={20} />
                  <p className="text-sm">{t('ops:drawer.press_play')}</p>
                  <p className="text-xs">{t('ops:drawer.paused_by_default')}</p>
                  <p className="text-xs text-status-stop max-w-md">{t('ops:drawer.paused_global_warning')}</p>
                </div>
              ) : events.length === 0 ? (
                <div className="flex items-center justify-center h-full text-slate-400 dark:text-slate-500">
                  <p className="text-sm">{t('ops:drawer.waiting_events')}</p>
                </div>
              ) : (
                // timeKey explícito: a tela de Eventos mostra e ordena pelo relógio do
                // DEVICE desde que a ingestão se revelou concorrente, e sem isto o
                // mesmo evento aparecia aqui com outra hora, no relógio de chegada.
                events.map((item, i) => <FeedItem key={item.feedKey ?? item.id ?? `adv-${i}`} item={item} label={nameOf(item.deviceName)} timeKey="device" />)
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
